import AppStorage from '@Services/AppStorage';

import {
  RECEIVED_RECENT_HISTORY,
  RECEIVED_SAVED_HISTORY,
  HISTORY_LOADING,
  HANDLE_VIEW_SAVE_MODAL,
  HANDLE_NAME_CHANGE,
  ON_SUBMIT_SAVE,
  ON_SUBMIT_COMPLETE,
  HANDLE_VIEW_DELETE_MODAL,
  ON_DELETE,
  ON_DELETE_COMPLETE,
  ON_SAVE_UNLOAD,
  ON_PAGE_CHANGE,
  HANDLE_CHECKBOX_CHANGE,
  TOGGLE_SELECT_ALL_ON_PAGE,
  HANDLE_SEARCH_INPUT,
} from './Constants';

export const initialState = {
  recentHistory: [],
  savedHistory: [],
  isRecentHistoryLoaded: false,
  isSavedHistoryLoaded: false,
  historyTypes: {},
  showSaveModal: false,
  itemToBeSaved: {},
  itemToBeSavedContext: '',
  saveModalTitle: '',
  saveModalNameInput: '',
  isInputTouched: false,
  isSaveInProgress: false,
  saveResult: {},
  error: {},
  itemTobeDeleted: {},
  showDeleteModal: false,
  isDeleteInProgress: false,
  itemIndex: 0,
  groupItemIndex: 0,
  currentPage: 0,
  pageSize: 15,
  totalItems: 0,
  recentTotalElements: 0,
  recentTotalPages: 0,
  selectedItems: [],
  searchInputText: '',
};

// A new query re-slices the result set, so the old page offset can land past the end and show an
// empty page while matches exist. Only reset when the query actually changed, so paging through
// results is not fought by re-dispatches of the same text.
function getPageForSearchInput(state, text) {
  const isNewQuery = (state.searchInputText || '') !== (text || '');
  return isNewQuery && state.currentPage > 1 ? 1 : state.currentPage;
}

// eslint-disable-next-line default-param-last
export default function historyReducer(state = initialState, action) {
  switch (action.type) {
    case HISTORY_LOADING: {
      return action.page === 'saved' ? { ...state, isSavedHistoryLoaded: false } : { ...state, isRecentHistoryLoaded: false };
    }
    case RECEIVED_RECENT_HISTORY: {
      const { recentHistory, historyTypes, totalElements, totalPages } = action;
      AppStorage.instance.setToSessionStorage('HistoryTypes', historyTypes);
      // Preserve currentPage so navigating to page 2 isn't reset back to 1.
      // Only initialise to 1 on the very first load (currentPage is 0).
      let currentPage = 0;
      if (state.currentPage > 0) {
        currentPage = state.currentPage;
      } else if ((totalElements ?? 0) > 0) {
        currentPage = 1;
      }
      return {
        ...state,
        recentHistory,
        historyTypes,
        isRecentHistoryLoaded: true,
        recentTotalElements: totalElements ?? 0,
        recentTotalPages: totalPages ?? 0,
        currentPage,
        selectedItems: [],
      };
    }
    case RECEIVED_SAVED_HISTORY: {
      const { savedHistory, historyTypes } = action;
      AppStorage.instance.setToSessionStorage('HistoryTypes', historyTypes);
      let totalItems = 0;
      // eslint-disable-next-line array-callback-return
      savedHistory.map((groupedItem) => {
        totalItems += groupedItem?.items?.length || 0;
      });
      return {
        ...state,
        savedHistory,
        historyTypes,
        isSavedHistoryLoaded: true,
        totalItems,
        currentPage: totalItems > 0 ? 1 : 0,
        selectedItems: [],
      };
    }
    case HANDLE_VIEW_SAVE_MODAL: {
      const { show, historyItem, itemToBeSavedContext, title, itemIndex, groupItemIndex } = action;
      return {
        ...state,
        showSaveModal: show,
        itemToBeSaved: { ...historyItem },
        itemToBeSavedContext: show ? itemToBeSavedContext : '',
        saveModalTitle: show ? title : '',
        isInputTouched: !show ? false : state.isInputTouched,
        saveModalNameInput: show && title === 'Rename' ? historyItem?.searchName : '',
        error: !show ? {} : state.error,
        saveResult: !show ? {} : state.saveResult,
        itemIndex,
        groupItemIndex,
      };
    }
    case HANDLE_NAME_CHANGE: {
      return { ...state, saveModalNameInput: action.value, isInputTouched: true };
    }
    case ON_SUBMIT_SAVE: {
      return { ...state, isSaveInProgress: true };
    }
    case ON_SUBMIT_COMPLETE: {
      const { groupItemIndex } = state;
      if (action.responseType === 'success' && !action.isRename) {
        return { ...state, isSaveInProgress: false, saveResult: { ...action.payload } };
      }
      if (action.responseType === 'success' && action.isRename) {
        const itemKey = action.renameItemKey;
        const getItemKey = (item = {}) => `${item?.id || ''}|${item?.databaseId || ''}|${item?.resultName || ''}`;
        const resolvedGroupItemIndex = itemKey
          ? state.savedHistory.findIndex((group) => group?.items?.some((item) => getItemKey(item) === itemKey))
          : groupItemIndex;
        const resolvedItemIndex =
          itemKey && resolvedGroupItemIndex >= 0
            ? state.savedHistory[resolvedGroupItemIndex].items.findIndex((item) => getItemKey(item) === itemKey)
            : state.itemIndex;
        const updatedSavedItems =
          state.savedHistory?.length > 0 && state.savedHistory[resolvedGroupItemIndex]?.items?.length > 0
            ? [...state.savedHistory[resolvedGroupItemIndex].items]
            : [];
        // Always use the new name from the action (not API response)
        const newName = action.newName || action?.payload?.searchName || updatedSavedItems[resolvedItemIndex]?.searchName;
        if (resolvedItemIndex >= 0 && newName?.trim().length > 0) {
          updatedSavedItems[resolvedItemIndex] = {
            ...updatedSavedItems[resolvedItemIndex],
            searchName: newName,
          };
        }
        if (resolvedGroupItemIndex < 0 || resolvedItemIndex < 0) {
          return { ...state, isSaveInProgress: false, saveResult: { ...action.payload } };
        }
        const updatedSavedHistory = [...state.savedHistory];
        updatedSavedHistory[resolvedGroupItemIndex] = {
          ...updatedSavedHistory[resolvedGroupItemIndex],
          items: [...updatedSavedItems],
        };
        return { ...state, isSaveInProgress: false, saveResult: { ...action.payload }, savedHistory: [...updatedSavedHistory] };
      }
      return { ...state, isSaveInProgress: false, error: { ...action.payload } };
    }
    case HANDLE_VIEW_DELETE_MODAL: {
      return {
        ...state,
        itemTobeDeleted: action.historyItem,
        showDeleteModal: action.show,
        itemIndex: action.itemIndex,
        groupItemIndex: action.groupItemIndex,
      };
    }
    case ON_DELETE: {
      return { ...state, isDeleteInProgress: true };
    }
    case ON_DELETE_COMPLETE: {
      const { groupItemIndex } = state;
      if (action.responseType === 'success') {
        const updatedSavedItems =
          state.savedHistory?.length > 0 &&
          state.savedHistory[groupItemIndex]?.items &&
          state.savedHistory[groupItemIndex].items?.length > 0
            ? [...state.savedHistory[groupItemIndex].items]
            : [];
        updatedSavedItems.splice(state.itemIndex, 1);
        const updatedSavedHistory = [...state.savedHistory];
        if (updatedSavedItems.length > 0) {
          updatedSavedHistory[groupItemIndex] = { ...updatedSavedHistory[groupItemIndex], items: [...updatedSavedItems] };
        } else {
          updatedSavedHistory.splice(groupItemIndex, 1);
        }
        return { ...state, isDeleteInProgress: false, savedHistory: [...updatedSavedHistory] };
      }
      return { ...state, isDeleteInProgress: false };
    }
    case ON_SAVE_UNLOAD: {
      return { ...state, isSavedHistoryLoaded: false };
    }
    case ON_PAGE_CHANGE: {
      // Defensive: always coerce to numbers and fallback to defaults
      const safePageSize = Number.isFinite(Number(action.pageSize)) && Number(action.pageSize) > 0 ? Number(action.pageSize) : 15;
      const safeCurrentPage =
        Number.isFinite(Number(action.currentPage)) && Number(action.currentPage) > 0 ? Number(action.currentPage) : 1;
      return {
        ...state,
        pageSize: safePageSize,
        currentPage: safeCurrentPage,
      };
    }
    case HANDLE_CHECKBOX_CHANGE: {
      const { resultName, isChecked } = action;
      const selectedItems = isChecked ? [...state.selectedItems, resultName] : state?.selectedItems?.filter((item) => item !== resultName);
      return {
        ...state,
        selectedItems,
      };
    }
    case TOGGLE_SELECT_ALL_ON_PAGE: {
      const { resultNames = [], isSelected } = action;
      const next = new Set(state.selectedItems);
      resultNames.forEach((resultName) => (isSelected ? next.add(resultName) : next.delete(resultName)));
      return { ...state, selectedItems: [...next] };
    }
    case HANDLE_SEARCH_INPUT: {
      return {
        ...state,
        searchInputText: action.text,
        currentPage: getPageForSearchInput(state, action.text),
      };
    }
    default: {
      return state;
    }
  }
}
